import { Box, CardContent, Stack, Typography, useTheme, Fade, Grow, Paper } from "@mui/material";

interface StatisticsCardProps {
  title: string;
  value: number | string;
  subtitle?: string;
  icon?: React.ReactNode;
  color?: "primary" | "secondary" | "success" | "error" | "warning" | "info";
}

const StatisticsCard = ({ title, value, subtitle, icon, color = "primary" }: StatisticsCardProps) => {
  const theme = useTheme();

  const getColorStyles = () => {
    const colors = {
      primary: {
        bg: theme.palette.primary.light,
        color: theme.palette.primary.main,
        icon: theme.palette.primary.dark,
      },
      secondary: {
        bg: theme.palette.secondary.light,
        color: theme.palette.secondary.main,
        icon: theme.palette.secondary.dark,
      },
      success: {
        bg: theme.palette.success.light,
        color: theme.palette.success.main,
        icon: theme.palette.success.dark,
      },
      error: {
        bg: theme.palette.error.light,
        color: theme.palette.error.main,
        icon: theme.palette.error.dark,
      },
      warning: {
        bg: theme.palette.warning.light,
        color: theme.palette.warning.main,
        icon: theme.palette.warning.dark,
      },
      info: {
        bg: theme.palette.info.light,
        color: theme.palette.info.main,
        icon: theme.palette.info.dark,
      },
    };
    return colors[color];
  };

  const colorStyles = getColorStyles();

  return (
    <Grow
      in
      timeout={800}
    >
      <Paper
        elevation={0}
        sx={{
          height: "100%",
          borderRadius: 3,
          background: `linear-gradient(145deg, ${theme.palette.background.paper} 0%, ${colorStyles.bg}15 50%, ${theme.palette.background.paper} 100%)`,
          border: `1px solid ${colorStyles.bg}30`,
          position: "relative",
          overflow: "hidden",
          transition: "all 0.2s linear",
          "&:before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "4px",
            transition: "all 0.2s linear",
            background: `linear-gradient(90deg, ${colorStyles.color} 0%, ${colorStyles.bg} 100%)`,
          },
          "&:hover": {
            transform: "translateY(-8px) scale(1.02)",
            boxShadow: `0 20px 40px ${colorStyles.bg}25`,
            "&:before": {
              height: "8px",
            },
          },
        }}
      >
        <CardContent sx={{ p: 3, position: "relative", zIndex: 1 }}>
          <Stack spacing={2.5}>
            <Stack
              direction="row"
              alignItems="flex-start"
              justifyContent="space-between"
              spacing={2}
            >
              <Box flex={1}>
                <Typography
                  variant="overline"
                  sx={{
                    color: colorStyles.color,
                    fontWeight: 700,
                    fontSize: "0.75rem",
                    letterSpacing: "0.1em",
                    textTransform: "uppercase",
                    mb: 1,
                    display: "block",
                  }}
                >
                  {title}
                </Typography>
                <Typography
                  variant="h3"
                  fontWeight={800}
                  color="text.primary"
                  sx={{
                    lineHeight: 1,
                    mb: 0.5,
                    background: `linear-gradient(135deg, ${theme.palette.text.primary} 0%, ${colorStyles.color} 100%)`,
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  {value}
                </Typography>
              </Box>
              {icon && (
                <Fade
                  in
                  timeout={1000}
                >
                  <Box
                    sx={{
                      background: `linear-gradient(135deg, ${colorStyles.color} 0%, ${colorStyles.bg}cc 100%)`,
                      color: "white",
                      borderRadius: 2.5,
                      p: 1.5,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: `0 8px 24px ${colorStyles.bg}40`,
                      transition: "all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
                      "&:hover": {
                        boxShadow: `0 12px 32px ${colorStyles.bg}60`,
                      },
                    }}
                  >
                    {icon}
                  </Box>
                </Fade>
              )}
            </Stack>
            {subtitle && (
              <Fade
                in
                timeout={1400}
              >
                <Box>
                  <Box
                    sx={{
                      height: "1px",
                      background: `linear-gradient(90deg, transparent 0%, ${colorStyles.bg}50 50%, transparent 100%)`,
                      mb: 1.5,
                    }}
                  />
                  <Box
                    sx={{
                      color: "text.secondary",
                      fontSize: "0.8rem",
                      fontWeight: 500,
                      opacity: 0.9,
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                    }}
                  >
                    <Box
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: "50%",
                        background: colorStyles.color,
                      }}
                    />
                    {subtitle}
                  </Box>
                </Box>
              </Fade>
            )}
          </Stack>
        </CardContent>
      </Paper>
    </Grow>
  );
};

export default StatisticsCard;
